// Inventory.js
// Player inventory with configurable slots and equipping support

import Pistol from '../weapons/Pistol.js';

export default class Inventory {
  constructor(slotCount = 4) {
    // Initialize slots as empty
    this.slots = new Array(slotCount).fill(null);
    this.equippedIndex = 0; // Start with the first slot selected
    
    // Create UI representation
    this.createInventoryUI();
    
    // Set up event listeners
    this.setupEventListeners();
    
    console.log("PlayerInventory initialized");
  }

  // Set up all event listeners
  setupEventListeners() {
    // Add mouse wheel event listener
    window.addEventListener('wheel', this.handleMouseWheel.bind(this));
    
    // Add number key event listener
    window.addEventListener('keydown', this.handleKeyDown.bind(this));
    
    // Listen for pistol level up events
    window.addEventListener('pistol-level-up', (event) => {
      console.log('PlayerInventory: Detected pistol-level-up event:', event.detail);
      
      // Force immediate UI update 
      this.updateUI();
      
      // Manually update level badge in case updateUI doesn't refresh it
      const slots = document.querySelectorAll('.inventory-slot');
      slots.forEach((slot, index) => {
        const item = this.slots[index];
        if (item && item.name === "Pistol") {
          // Remove existing badge
          const existingBadge = slot.querySelector('.level-badge');
          if (existingBadge) {
            existingBadge.remove();
          }
          
          // Add updated badge
          const skillLevel = event.detail.level;
          const levelBadge = document.createElement('div');
          levelBadge.className = 'level-badge';
          levelBadge.textContent = `Lv${skillLevel}`;
          levelBadge.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            background: rgba(255, 215, 0, 0.7);
            color: black;
            font-size: 10px;
            padding: 2px 4px;
            border-radius: 2px;
            font-weight: bold;
            z-index: 2;
          `;
          slot.appendChild(levelBadge);
          
          console.log('PlayerInventory: Updated pistol level badge to:', skillLevel);
        }
      });
    });
  }

  // Handle mouse wheel events to cycle through inventory
  handleMouseWheel(event) {
    // Prevent default scrolling behavior
    event.preventDefault();
    
    // Determine scroll direction
    if (event.deltaY > 0) {
      // Scrolling down - go to next slot
      this.cycleNext();
    } else if (event.deltaY < 0) {
      // Scrolling up - go to previous slot
      this.cyclePrevious();
    }
  }
  
  // Handle number key presses for direct slot selection
  handleKeyDown(event) {
    // Check if pressed key is a number between 1-4 (or however many slots we have)
    const keyNum = parseInt(event.key);
    if (!isNaN(keyNum) && keyNum >= 1 && keyNum <= this.slots.length) {
      const oldIndex = this.equippedIndex;
      const newIndex = keyNum - 1; // Convert to 0-based index
      
      // Only update if the index actually changed
      if (oldIndex !== newIndex) {
        // Unequip current item if needed
        const currentItem = this.getEquipped();
        if (currentItem && typeof currentItem.unequip === 'function') {
          currentItem.unequip();
        }
        
        // Set new equipped index
        this.setEquippedIndex(newIndex);
        
        // Equip new item if needed
        const newItem = this.getEquipped();
        if (newItem && typeof newItem.equip === 'function') {
          newItem.equip();
        }
      }
    }
  }

  addItem(item, slot = 0) {
    if (slot < 0 || slot >= this.slots.length) {
      console.error(`Invalid slot index: ${slot}`);
      return false;
    }
    
    this.slots[slot] = item;
    this.updateUI();
    return true;
  }

  getEquipped() {
    const equipped = this.slots[this.equippedIndex];
    // Add debug logging for equipped item
    console.log("PlayerInventory.getEquipped() called, slot:", this.equippedIndex, 
                "item:", equipped ? equipped.name : "empty");
    return equipped;
  }

  setEquippedIndex(index) {
    if (index >= 0 && index < this.slots.length) {
        const previous = this.getEquipped();
        if (previous && typeof previous.unequip === 'function') {
            console.log(`Unequipping item in slot ${this.equippedIndex}: ${previous.name}`);
            previous.unequip();
        }

        this.equippedIndex = index;

        const selected = this.getEquipped();
        if (selected && typeof selected.equip === 'function') {
            console.log(`Equipping item in slot ${index}: ${selected.name}`);
            selected.equip();
        } else {
            console.log(`No equip() method found for item in slot ${index}`);
        }

        this.updateUI();
        return true;
    }
    return false;
  }

  useEquipped() {
    const item = this.getEquipped();
    if (item && typeof item.fire === 'function' && item.equipped) {
      return item.fire();
    }
    return false;
  }

  cycleNext() {
    const oldIndex = this.equippedIndex;
    this.equippedIndex = (this.equippedIndex + 1) % this.slots.length;
    
    // Handle equipment changes
    this._handleEquipmentChange(oldIndex, this.equippedIndex);
    
    this.updateUI();
    return this.getEquipped();
  }
  
  cyclePrevious() {
    const oldIndex = this.equippedIndex;
    this.equippedIndex = (this.equippedIndex - 1 + this.slots.length) % this.slots.length;
    
    // Handle equipment changes
    this._handleEquipmentChange(oldIndex, this.equippedIndex);
    
    this.updateUI();
    return this.getEquipped();
  }
  
  // Helper method to handle unequipping/equipping during changes
  _handleEquipmentChange(oldIndex, newIndex) {
    // Only process if indices are different
    if (oldIndex === newIndex) return;

    // Unequip the previously selected item
    const oldItem = this.slots[oldIndex];
    if (oldItem && typeof oldItem.unequip === 'function') {
        oldItem.unequip();
        console.log(`Unequipped item at slot ${oldIndex + 1}: ${oldItem.name}`);
    }

    // Equip the newly selected item
    const newItem = this.slots[newIndex];
    if (newItem && typeof newItem.equip === 'function') {
        newItem.equip();
        console.log(`Equipped item at slot ${newIndex + 1}: ${newItem.name}`);
    } else {
        console.log(`Slot ${newIndex + 1} is empty or item cannot be equipped`);
    }
  }
  
  // Create UI elements for inventory
  createInventoryUI() {
    // Remove previous UI if it exists
    if (document.getElementById('inventoryContainer')) {
      document.getElementById('inventoryContainer').remove();
    }
    
    // Create inventory container
    const container = document.createElement('div');
    container.id = 'inventoryContainer';
    container.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 10px;
      background: rgba(0, 0, 0, 0.6);
      padding: 10px;
      border-radius: 8px;
      z-index: 100;
    `;
    
    // Create slots
    this.slotElements = [];
    for (let i = 0; i < this.slots.length; i++) {
      const slot = document.createElement('div');
      slot.className = 'inventory-slot';
      slot.dataset.index = i;
      
      // Style the slot
      slot.style.cssText = `
        width: 60px;
        height: 60px;
        background: rgba(80, 80, 80, 0.5);
        border: 2px solid #555;
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        position: relative;
      `;
      
      // Add slot number
      const slotNumber = document.createElement('div');
      slotNumber.className = 'slot-number';
      slotNumber.textContent = (i + 1).toString();
      slotNumber.style.cssText = `
        position: absolute;
        bottom: 2px;
        right: 5px;
        font-size: 12px;
        color: white;
        font-weight: bold;
        font-family: Arial, sans-serif;
      `;
      slot.appendChild(slotNumber);
      
      // Add image container for item
      const itemImage = document.createElement('div');
      itemImage.className = 'item-image';
      itemImage.style.cssText = `
        width: 50px;
        height: 35px;
        display: flex;
        justify-content: center;
        align-items: center;
      `;
      slot.appendChild(itemImage);
      
      // Add item name element
      const itemName = document.createElement('div');
      itemName.className = 'item-name';
      itemName.style.cssText = `
        width: 100%;
        font-size: 10px;
        color: white;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-top: 2px;
        padding: 0 2px;
        font-family: Arial, sans-serif;
      `;
      slot.appendChild(itemName);
      
      container.appendChild(slot);
      this.slotElements.push(slot);
    }
    
    document.body.appendChild(container);
    
    // Initial update to show equipped item
    this.updateUI();
  }
  
  // Update UI to reflect current inventory state
  updateUI() {
    if (!this.slotElements) return;
    
    // Update each slot
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slotElements[i];
      const item = this.slots[i];
      
      // Clear previous item image and level badge
      const itemImage = slot.querySelector('.item-image');
      itemImage.innerHTML = '';
      
      // Remove any existing level badges
      const existingBadges = slot.querySelectorAll('.level-badge');
      existingBadges.forEach(badge => badge.remove());
      
      // Update item name
      const itemNameElement = slot.querySelector('.item-name');
      itemNameElement.textContent = item ? item.name : 'Empty';
      
      // Add item image if slot has an item
      if (item) {
        // Create SVG representation of item
        if (item.name === "Pistol") {
          const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          svg.setAttribute("viewBox", "0 0 100 100");
          svg.setAttribute("width", "40");
          svg.setAttribute("height", "40");
          
          // Gun body
          const gunBody = document.createElementNS("http://www.w3.org/2000/svg", "rect");
          gunBody.setAttribute("x", "30");
          gunBody.setAttribute("y", "40");
          gunBody.setAttribute("width", "40");
          gunBody.setAttribute("height", "15");
          gunBody.setAttribute("fill", "#333");
          svg.appendChild(gunBody);
          
          // Gun handle
          const gunHandle = document.createElementNS("http://www.w3.org/2000/svg", "rect");
          gunHandle.setAttribute("x", "50");
          gunHandle.setAttribute("y", "55");
          gunHandle.setAttribute("width", "10");
          gunHandle.setAttribute("height", "25");
          gunHandle.setAttribute("fill", "#555");
          svg.appendChild(gunHandle);
          
          // Gun barrel
          const gunBarrel = document.createElementNS("http://www.w3.org/2000/svg", "rect");
          gunBarrel.setAttribute("x", "20");
          gunBarrel.setAttribute("y", "42");
          gunBarrel.setAttribute("width", "20");
          gunBarrel.setAttribute("height", "10");
          gunBarrel.setAttribute("fill", "#222");
          svg.appendChild(gunBarrel);
          
          itemImage.appendChild(svg);
          
          // Add item level if applicable - get level from pistolSkill if available
          if (window.pistolSkill && item.name === "Pistol") {
            const skillLevel = window.pistolSkill.level;
            console.log(`Inventory: Updating Pistol level to ${skillLevel}`);
            const levelBadge = document.createElement('div');
            levelBadge.className = 'level-badge';
            levelBadge.textContent = `Lv${skillLevel}`;
            levelBadge.style.cssText = `
              position: absolute;
              top: 0;
              left: 0;
              background: rgba(255, 215, 0, 0.7);
              color: black;
              font-size: 10px;
              padding: 2px 4px;
              border-radius: 2px;
              font-weight: bold;
              z-index: 2;
            `;
            slot.appendChild(levelBadge);
          }
        }
      }
      
      // Highlight the equipped slot
      if (i === this.equippedIndex) {
        slot.style.border = "2px solid #ffcc00";
        slot.style.boxShadow = "0 0 10px #ffcc00";
        slot.style.background = "rgba(120, 120, 80, 0.5)";
      } else {
        slot.style.border = "2px solid #555";
        slot.style.boxShadow = "none";
        slot.style.background = "rgba(80, 80, 80, 0.5)";
      }
    }
  }

  getEquippedName() {
    const item = this.getEquipped();
    return item ? item.name || "Item" : "Empty";
  }

  getEquippedIcon() {
    const item = this.getEquipped();
    return item ? item.icon || "" : "";
  }
}
